import { Injectable } from "@angular/core";
import { AlertController, LoadingController, NavController } from "ionic-angular";
import { Chapter } from "../models/Chapter";
import { Entry } from "../models/entry";
import { BookmarkPage } from "../pages/bookmark/bookmark";
import { BrowseCategoryPage } from "../pages/browse-category/browse-category";
import { LoginPage } from "../pages/login/login";
import { RegisterPage } from "../pages/register/register";
import { SubscriptionsPage } from "../pages/subscriptions/subscriptions";
import { Api } from "./api";
import { ForbiddenError } from "./api/errors/ForbiddenError";
import { UnauthorizedError } from "./api/errors/UnauthorizedError";
import { SubscriptionService } from "./subscription-service";
import { DesktopService } from "./desktop-service";
import { EntryPage } from "../pages/desktop/entry/entry";
import {BlankDesktopPage} from "../pages/desktop/blank/blank";

export interface Controllers {
    alertCtrl: AlertController,
    loadingCtrl: LoadingController,
    navCtrl: NavController
}

export interface Pages {
    loginPage: any;
    registerPage: any;
}

@Injectable()
export class DirectoryService {

    private alertCtrl: AlertController;
    private loadingCtrl: LoadingController;
    private navCtrl: NavController;
    private loginPage: any;
    private registerPage: any;

    constructor(
        private api: Api,
        private subscriptionService: SubscriptionService,
        private desktopService: DesktopService,
    ) {

    }

    public getAlertCtrl(): AlertController {
        return this.alertCtrl;
    }

    public getControllers(): Controllers {
        return {
            alertCtrl: this.getAlertCtrl(),
            loadingCtrl: this.getLoadingCtrl(),
            navCtrl: this.getNavCtrl(),
        }
    }

    public getLoadingCtrl(): LoadingController {
        return this.loadingCtrl;
    }

    public getLoginPage(): any {
        return this.loginPage;
    }

    public getNavCtrl(): NavController {
        return this.navCtrl;
    }

    public getPages(): Pages {
        return {
            loginPage: this.loginPage,
            registerPage: this.registerPage,
        }
    }

    public getRegisterPage(): any {
        return this.registerPage;
    }

    public setAlertCtrl(alertCtrl: AlertController) {
        this.alertCtrl = alertCtrl;
    }

    public setControllers(controllers: Partial<Controllers>) {
        if (controllers.alertCtrl) this.setAlertCtrl(controllers.alertCtrl);
        if (controllers.loadingCtrl) this.setLoadingCtrl(controllers.loadingCtrl);
        if (controllers.navCtrl) this.setNavCtrl(controllers.navCtrl);
    }

    public setLoadingCtrl(loadingCtrl: LoadingController) {
        this.loadingCtrl = loadingCtrl;
    }

    public setLoginPage(loginPage: any) {
        this.loginPage = loginPage;
    }

    public setNavCtrl(navCtrl: NavController) {
        this.navCtrl = navCtrl;
    }

    public setPages(pages: Partial<Pages>) {
        if (pages.loginPage) this.setLoginPage(pages.loginPage);
        if (pages.registerPage) this.setRegisterPage(pages.registerPage);
    }

    public setRegisterPage(registerPage: any) {
        this.registerPage = registerPage;
    }

    public showChapter(chapter: Chapter) {
        let loading = this.loadingCtrl.create();
        loading.present();

        this.api.getChapterId(chapter.id).finally(() => {
            loading.dismiss();
        }).subscribe(async result => {
            await this.navCtrl.push(BrowseCategoryPage, {
                chapter: result.chapter,
                entries: result.entries
            });
            if (this.desktopService.isDesktop() == true) {
                await this.desktopService.getRightPane().setRoot(BlankDesktopPage, {
                    advert: result.advert
                }, {
                    animate: true,
                    animation: 'md-transition',
                });
            }
        }, error => {
            if (error instanceof UnauthorizedError) {
                return this.showLoginRequiredAlert('You must be logged in to view the "' + chapter.title + '" chapter');
            }

            if (error instanceof ForbiddenError) {
                return this.subscriptionService.showSubscriptionRequired(this.getNavCtrl(), SubscriptionsPage);
            }

            return this.showError('An error occurred while trying to fetch the "' + chapter.title + '" chapter');
        });
    }

    public showEntry(entry: Entry) {
        let loading = this.loadingCtrl.create();
        loading.present();

        this.api.getEntryById(entry.id).finally(() => {
            loading.dismiss();
        }).subscribe(result => {
            let contentBlocks = result.content_blocks;

            const params = {
                entries: result.entries,
                entry: entry
            };

            if (this.desktopService.isDesktop()) {
                if (contentBlocks.length <= 0) {
                    this.desktopService.getLeftPane().push(BrowseCategoryPage, params);
                } else {
                    if (params.entries.length > 0) {
                        this.desktopService.getLeftPane().push(BrowseCategoryPage, params);
                    }

                    this.desktopService.getRightPane().setRoot(EntryPage, {
                        content_blocks: contentBlocks,
                        advert: result.advert,
                        ...params
                    });
                }
            } else {
                if (contentBlocks.length <= 0) {
                    this.navCtrl.push(BrowseCategoryPage, params);
                } else {
                    this.navCtrl.push(BookmarkPage, {
                        content_blocks: contentBlocks,
                        ...params
                    });
                }
            }
        }, error => {
            if (error instanceof UnauthorizedError) {
                return this.showLoginRequiredAlert('You must be logged in to view "' + entry.title + '"');
            }

            if (error instanceof ForbiddenError) {
                return this.subscriptionService.showSubscriptionRequired(this.getNavCtrl(), SubscriptionsPage);
            }

            return this.showError('An error occured while trying to fetch "' + entry.title + '"');
        });
    }

    public async showError(message: string = 'An error has occurred') {
        let alert = this.alertCtrl.create({
            title: 'Error',
            message: message,
            buttons: [{
                text: 'OK'
            }]
        });

        await alert.present();

        return alert;
    }

    public showLogin() {
        console.log(this.getLoginPage());
        this.navCtrl.push(this.getLoginPage());
    }

    public async showLoginRequiredAlert(message: string = 'You must be logged in to view this content') {
        let alert = this.alertCtrl.create({
            title: 'Login Required',
            message: message,
            buttons: [{
                text: 'Login',
                handler: () => {
                    this.showLogin()
                },
            }, {
                text: 'Register',
                handler: () => {
                    this.showRegister()
                },
            }, {
                text: 'Cancel',
            }]
        });

        await alert.present();

        return alert;
    }

    public showRegister() {
        this.navCtrl.push(this.getRegisterPage());
    }

}



// WEBPACK FOOTER //
// ./src/providers/directory-service.ts